import React from 'react';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  moveGallectionToPosition,
  MoveGallectionToPositionPayload,
} from '../../actions';
import GallectionGrid from '../GallectionGrid';
import PopupInnerLayout from '../../_partials/PopupInnerLayout';
import FolderCard from '../../_partials/FolderCard';
import { isSpecialStuckGallection } from '../../helpers';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import GallectionControlButtons from '../../_partials/GallectionControlButtons';
import AddGallectionButton from '../../_partials/AddGallectionButton';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import withCurrentMediaSource, {
  WithCurrentMediaSourceProps,
} from '../../_hocs/withCurrentMediaSource';
import { MediaSourceType } from '../../types';
import withGlobalPrivs, {
  WithGlobalPrivsProps,
} from '../../_hocs/withGlobalPrivs';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-react-context/pkg/flags/withFeatureFlags';
import { Url } from '@wix/da-url';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import Flex from '@wix/da-ds/pkg/Flex';

import s from './GallectionManager.scss';

export interface Props {}

const enhance = compose(
  withGallections,
  withCurrentGallection,
  withCurrentMediaSource,
  withGlobalPrivs,
  withFeatureFlags,
  connectMediaStore(null, { moveGallectionToPosition }),
  withTranslation()
);
export class GallectionManager extends React.Component<
  Props &
    WithGallectionsProps &
    WithCurrentGallectionProps &
    WithCurrentMediaSourceProps &
    WithGlobalPrivsProps &
    WithFeatureFlagsProps &
    WithTranslation & {
      moveGallectionToPosition(payload: MoveGallectionToPositionPayload): any;
    }
> {
  existingGallectionNames: string[];
  render() {
    return (
      <PopupInnerLayout
        headerSubtitle={this.getSubtitle()}
        headerExtrasRightZone={this.renderRightZoneButtons()}
        scrollableContent={this.renderContent()}
        onScrolledToBottom={this.fetchMoreGallectionsIfNeeded}
        onNotScrollable={this.fetchMoreGallectionsIfNeeded}
      />
    );
  }

  renderRightZoneButtons() {
    const { currentMediaSource, currentGallection, t } = this.props;

    const isRootView = !currentGallection || !currentGallection?.parentId;
    const showGallerySettings =
      isRootView && currentMediaSource === MediaSourceType.GALLERY;

    return (
      <Flex style={{ gap: 16 }}>
        {showGallerySettings && (
          <IconButtonWithContextMenu
            icon={EllipsisIcon}
            withBackground
            items={[
              {
                label: t('galleryDisplaySettings.menuItem'),
                link: `${Url.studioGallery()}?settings=1`,
              },
            ]}
            size="medium"
            aria-label={t('common.moreActions')}
            className={s['gallery-settings-button']}
          />
        )}
        <AddGallectionButton />
      </Flex>
    );
  }

  getSubtitle() {
    const { t, currentMediaSource, canRearrangeGalleryFolders } = this.props;
    switch (currentMediaSource) {
      case MediaSourceType.STASH:
        return t('mediapopup.editYourStashFoldersHint');
      case MediaSourceType.GALLERY:
        return canRearrangeGalleryFolders
          ? t('mediapopup.editYourGalleriesHint')
          : '';
      case MediaSourceType.PRIVATE_COLLECTION:
        return t('mediapopup.editYourPrivateCollectionsHint');
      case MediaSourceType.COLLECTION:
      default:
        return t('mediapopup.editYourCollectionsHint');
    }
  }

  renderContent() {
    const {
      isGallectionStreamInitialized,
      gallections,
      hasMoreGallections,
      moveGallectionToPosition: moveGallection,
    } = this.props;

    this.existingGallectionNames = this.props.gallections.map(
      gallection => gallection.name
    );

    return (
      <>
        {isGallectionStreamInitialized && (
          <GallectionGrid
            cookieType={MeasuredCookieType.GALLECTION_MANAGER}
            gallections={gallections}
            gallectionRenderer={this.renderGallection}
            moveGallectionToPosition={moveGallection}
          />
        )}
        {hasMoreGallections && (
          <LoadingIndicator
            view="daSymbol"
            coverMode={!gallections.length && hasMoreGallections}
          />
        )}
      </>
    );
  }

  fetchMoreGallectionsIfNeeded = () => {
    const { hasMoreGallections, isFetchingGallections, fetchMoreGallections } =
      this.props;
    if (!hasMoreGallections || isFetchingGallections) {
      return;
    }
    fetchMoreGallections();
  };

  renderGallection = ({ index, width }) => {
    const { gallections } = this.props;
    const gallection = gallections[index];

    const extras = (
      <GallectionControlButtons
        gallection={gallection}
        existingGallectionNames={this.existingGallectionNames}
        className={s['folder-card-controls']}
        moreMenuClassName={s['folder-card-controls-menu']}
      />
    );

    return (
      <FolderCard
        className={s['folder-card']}
        width={width}
        folder={gallection}
        isLocked={isSpecialStuckGallection(gallection)}
        extras={extras}
        withPrice={true}
        disableMarginRight={true}
      />
    );
  };
}

export default enhance(GallectionManager) as any as React.ComponentClass<Props>;
